import { ChevronLeft, ChevronRight } from 'lucide-react'

// ─────────────────────────────────────────────────────────────────────────────
// ScrollChevron — the round chevron button used to step a tab strip left/right.
// The button chrome (border, bg, shadow) stays fully opaque so the control is
// always clearly visible; only the chevron glyph dims when disabled (at the edge).
// ─────────────────────────────────────────────────────────────────────────────
interface ScrollChevronProps {
    side: 'left' | 'right'
    /** When true the chevron is dimmed and non-interactive (already at this edge). */
    disabled: boolean
    onScroll: () => void
    className?: string
}

export const ScrollChevron = ({ side, disabled, onScroll, className = '' }: ScrollChevronProps) => {
    const isLeft = side === 'left'
    return (
        <button
            type="button"
            onClick={onScroll}
            disabled={disabled}
            aria-label={isLeft ? 'Scroll tabs left' : 'Scroll tabs right'}
            className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-md transition-all enabled:cursor-pointer enabled:text-slate-500 dark:enabled:text-slate-400 enabled:hover:bg-slate-50 dark:enabled:hover:bg-slate-700 enabled:hover:text-slate-700 dark:enabled:hover:text-slate-200 enabled:hover:shadow-lg disabled:cursor-default disabled:text-slate-400 dark:disabled:text-slate-500 ${className}`}
        >
            {isLeft ? <ChevronLeft className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// TabScrollEdge — a fade gradient pinned to the left edge of a scrolling tab
// strip, with a ScrollChevron on top. Used only on the scroll-side edge (left),
// where overlaying the tabs is desired; the gradient (pointer-events-none) hints
// at hidden content without blocking tab clicks.
// ─────────────────────────────────────────────────────────────────────────────
interface TabScrollEdgeProps {
    side: 'left' | 'right'
    visible: boolean
    disabled: boolean
    onScroll: () => void
}

export const TabScrollEdge = ({ side, visible, disabled, onScroll }: TabScrollEdgeProps) => {
    if (!visible) return null
    const isLeft = side === 'left'
    return (
        <div className={`pointer-events-none absolute inset-y-0 ${isLeft ? 'left-0 justify-start pl-1' : 'right-0 justify-end pr-1'} flex items-center`}>
            <div
                aria-hidden
                className={`absolute inset-0 transition-opacity ${disabled ? 'opacity-0' : 'opacity-100'} ${
                    isLeft ? 'bg-gradient-to-r from-white dark:from-slate-900 to-transparent' : 'bg-gradient-to-l from-white dark:from-slate-900 to-transparent'
                }`}
            />
            <ScrollChevron side={side} disabled={disabled} onScroll={onScroll} className="pointer-events-auto relative" />
        </div>
    )
}
